import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ValidationError } from '#api/errors.ts';
import { makeNetworkQueryOptions } from '#api/networks.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useDeleteNetworkMutation } from './useDeleteNetworkMutation.ts';

type Props = {
  networkId: string;
  onCancel: () => void;
  onDelete: () => void;
};

/**
 * If we have determined that there are no hosts still in this network, we show this confirmation
 * to be sure the user really wants to delete the network.
 *
 * We also have some validation error handling here as well, just in case something slips through,
 * but we don't expect validation failures at this point.
 */
export function DeleteNetworkConfirmation({ networkId, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteNetwork = useDeleteNetworkMutation();
  const { data: { data: network } = {} } = useQuery({
    ...makeNetworkQueryOptions(networkId),
    // HACK: there's a single render after deleting, which causes a 404.  We avoid that here
    enabled: !deleteNetwork.isSuccess,
  });

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  if (!network) return null;

  return (
    <>
      <ModalBody>
        <p className="p">
          Are you sure you want to delete this network? This action cannot be undone and will remove all associated
          data.
        </p>
        <ul className="ul">
          <li className="p">
            Network name: <strong className="wrap-anywhere">{network.name}</strong>
          </li>
          {network.cidrs.map((cidr) => (
            <li className="p" key={cidr}>
              CIDR range: <strong>{cidr}</strong>
            </li>
          ))}
        </ul>

        {validationErrors?.length ?
          validationErrors.map((message, index) => (
            // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
            <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
              {message}
            </ErrorMessage>
          ))
        : null}
      </ModalBody>
      <ModalFooter>
        <Spacer>
          <Button variant="link" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setValidationErrors(null);

              try {
                await deleteNetwork.mutateAsync(networkId, { onSuccess: onDelete });
              } catch (error: unknown) {
                if (error instanceof ValidationError) {
                  setValidationErrors(error.errors.map((e) => e.message));
                }
              }
            }}
            disabled={deleteNetwork.isPending}
          >
            Delete
          </Button>
        </Spacer>
      </ModalFooter>
    </>
  );
}
